import { useAuth } from '@simplifi/auth'
import type { LDClient, LDMultiKindContext } from 'launchdarkly-js-client-sdk'
import { ldInit, useLDFlag } from 'launchdarkly-vue-client-sdk'

let client: LDClient | undefined = undefined

export async function init() {
  if (client) {
    return
  }

  const { session, loggedIn } = useAuth()
  if (!loggedIn) {
    throw new Error('Attempted to initialize LaunchDarkly without a session')
  }

  const context: LDMultiKindContext = {
    kind: 'multi',
    user: {
      anonymous: false,
      key: `${session.value.userId}`,
      name: session.value.userName,
      email: session.value.email,
    },
  }

  try {
    const [, newClient] = ldInit({ context })
    client = newClient
    client.on('failed', (err) => {
      console.error('LD Failed to init', err)
    })
    client.on('error', (err) => {
      console.error('LD Errored on init', err)
    })
    await client.waitUntilReady()

    return client
  } catch (error) {
    console.error('LD Failed to init', error)
  }
}

export function useFlag(flagName: string, defaultValue = false) {
  try {
    return useLDFlag(flagName, defaultValue)
  } catch {
    return defaultValue
  }
}

export const flag = {
  appAccess: () => useFlag('wip-246-progress-app'),
  siDashboard: () => useFlag('wip-647-si-dashboard'),
  auditingDashboard: () => useFlag('wip-661-operations-auditing-dashboard'),
  calculatedStatuses: () => useFlag(flagName.calculatedStatuses),
  bestEtaIcon: () => useFlag('fnol-725-beta-icon-in-ops'),
  alignedTimers: () => useFlag(flagName.alignedTimers),
  messagingAccess: () => useFlag(flagName.messagingAccess),
  mailboxDateRangeSync: () => useFlag(flagName.mailboxDateRangeSync),
  mercuriOndemandEmails: () => useFlag(flagName.mercuriOndemandEmails),
  messageTemplates: () => useFlag(flagName.messageTemplates),
}

// Note: For use outside of Vue components using useLDClient()
export const flagName = {
  calculatedStatuses: 'wip-929-ops-calculated-statuses',
  alignedTimers: 'wip-907-aligned-timers',
  messagingAccess: 'fnol-833-messaging-access',
  mailboxDateRangeSync: 'sp-97-mailbox-date-range-sync',
  mercuriOndemandEmails: 'sp-130-mercuri-ondemand-emails',
  messageTemplates: 'sp-148-message-templates',
}
